import { useEffect, useRef, useState } from 'react';

/**
 * DS guidelines/motion.md, "Waiting and loading": an animated indicator shows
 * only once a wait passes about 400 ms, and once shown it stays at least about
 * 500 ms, so a fast answer never flashes a spinner. The DS ships these as
 * prose, not tokens, so they live here once.
 */
export const PROGRESS_SHOW_AFTER_MS = 400;
export const PROGRESS_MIN_VISIBLE_MS = 500;

/**
 * Whether the "Buscando…" indicator is on screen for a wait.
 *
 * @param busy - Whether something is being waited on right now.
 * @returns True from 400 ms into a wait until the wait ends, and never for
 * less than 500 ms once shown.
 */
export default function useDeferredProgress(busy: boolean): boolean {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (busy && !visible) {
      const timer = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, PROGRESS_SHOW_AFTER_MS);

      return () => clearTimeout(timer);
    }

    if (!busy && visible) {
      const left = PROGRESS_MIN_VISIBLE_MS - (Date.now() - shownAtRef.current);
      const timer = setTimeout(() => setVisible(false), Math.max(left, 0));

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [busy, visible]);

  return visible;
}
